import React, { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Paper,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Home, Calendar, Users, UserPlus, CheckCircle, XCircle, Settings } from 'lucide-react';
import { ThemeModeContext } from './ThemeProvider';
import { notifyUnitsChange } from '../hooks/useUnits';
import { useTranslation } from 'react-i18next';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { markWelcomeTourCompleted, getUser, AppUser, updateUserLanguagePreference } from '../utils/userUtils';
import { submitUnclaimProfileRequest } from '../services/claimProfileService';

interface TourStep {
  titleKey: string;
  descriptionKey: string;
  icon: React.ReactNode;
  highlight?: string;
  condition?: 'always' | 'not-merged' | 'merged';
}

const WelcomeTour: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<AppUser | null>(null);
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [preferencePersisted, setPreferencePersisted] = useState(false);
  const [preferenceSaving, setPreferenceSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false); // Prevents re-opening after user dismisses
  
  // Profile confirmation state (for auto-matched users)
  const [profileConfirmation, setProfileConfirmation] = useState<'yes' | 'no'>('yes');
  const [unclaimSubmitting, setUnclaimSubmitting] = useState(false);
  const [unclaimError, setUnclaimError] = useState<string | null>(null);
  const [unclaimSuccess, setUnclaimSuccess] = useState(false);
  
  // Settings state
  const [uiLanguage, setUiLanguage] = useState<'en' | 'no' | 'fr'>('no');
  const [units, setUnits] = useState<'km' | 'mi'>('km');
  const [settingsSaved, setSettingsSaved] = useState(false);
  const { mode: themeMode, setMode: setThemeMode } = useContext(ThemeModeContext);
  
  // Profile refresh state for automatch-dependent panels
  const [isRefreshingProfile, setIsRefreshingProfile] = useState(false);


  const iconStyle = { width: 48, height: 48 };

  // Check if user was auto-merged (has personId means they were linked to existing runner data)
  // Also check hasCompletedUsersAutomatch to handle race condition with trigger
  // For legacy users where the field doesn't exist, assume automatch is complete if personId exists
  const wasAutoMerged = userProfile?.personId != null;
  // const automatchComplete = userProfile?.hasCompletedUsersAutomatch === true || 
//   userProfile?.personId != null ||  // If personId exists, automatch was definitely done
//   (userProfile?.hasCompletedUsersAutomatch === undefined && userProfile?.createdAt);  // Legacy users

  // Define all possible tour steps with conditions
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allTourSteps: TourStep[] = [
    {
      titleKey: 'welcomeTour.welcome.title',
      descriptionKey: 'welcomeTour.welcome.description',
      icon: <Home style={{ ...iconStyle, color: theme.palette.primary.main }} />,
      condition: 'always',
    },
    {
      titleKey: 'welcomeTour.events.title',
      descriptionKey: 'welcomeTour.events.description',
      icon: <Calendar style={{ ...iconStyle, color: theme.palette.primary.main }} />,
      highlight: 'nav-events',
      condition: 'always',
    },
    {
      titleKey: 'welcomeTour.runners.title',
      descriptionKey: 'welcomeTour.runners.description',
      icon: <Users style={{ ...iconStyle, color: theme.palette.primary.main }} />,
      highlight: 'nav-runners',
      condition: 'always',
    },
    {
      // Show this only for users who were NOT auto-merged
      titleKey: 'welcomeTour.claimProfile.title',
      descriptionKey: 'welcomeTour.claimProfile.description',
      icon: <UserPlus style={{ ...iconStyle, color: theme.palette.secondary.main }} />,
      condition: 'not-merged',
    },
    {
      // Show this only for users who WERE auto-merged
      titleKey: 'welcomeTour.profileLinked.title',
      descriptionKey: 'welcomeTour.profileLinked.description',
      icon: <CheckCircle style={{ ...iconStyle, color: theme.palette.success.main }} />,
      condition: 'merged',
    },
    {
      // Settings step - shown for all users
      titleKey: 'welcomeTour.settings.title',
      descriptionKey: 'welcomeTour.settings.description',
      icon: <Settings style={{ ...iconStyle, color: theme.palette.info.main }} />,
      condition: 'always',
    },
    {
      titleKey: 'welcomeTour.done.title',
      descriptionKey: 'welcomeTour.done.description',
      icon: <CheckCircle style={{ ...iconStyle, color: theme.palette.success.main }} />,
      condition: 'always',
    },
  ];

  // Filter steps based on merge status
  const tourSteps = useMemo(() => {
    return allTourSteps.filter(step => {
      if (step.condition === 'always') return true;
      if (step.condition === 'merged') return wasAutoMerged;
      if (step.condition === 'not-merged') return !wasAutoMerged;
      return true;
    });
  }, [wasAutoMerged, allTourSteps]);

  // Listen for auth state changes and load user profile
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      setCurrentUser(u);
      if (u) {
        try {
          const profile = await getUser(u.uid);
          setUserProfile(profile);
        } catch (err) {
          console.warn('[WelcomeTour] Failed to get user profile:', err);
        }
      } else {
        setUserProfile(null);
      }
    });
    return unsub;
  }, []);

  // Show tour immediately for new users who haven't completed it
  // Don't wait for automatch - we'll refresh profile when reaching profile-dependent panels
  useEffect(() => {
    if (currentUser && userProfile && userProfile.hasCompletedWelcomeTour === false && !open && !dismissed) {
      setOpen(true);
      // Initialize UI language from i18n current language
      const currentLang = i18n.language?.substring(0, 2) as 'en' | 'no' | 'fr';
      if (currentLang === 'en' || currentLang === 'no' || currentLang === 'fr') {
        setUiLanguage(currentLang);
      } else {
        setUiLanguage('no');
      }
      // Initialize units from localStorage
      const storedUnits = localStorage.getItem('pref_units');
      if (storedUnits === 'mi') {
        setUnits('mi');
      }
    }
  }, [currentUser, userProfile, open, dismissed, i18n.language]);

  // Refresh user profile - used when reaching profile-dependent panels
  const refreshUserProfile = useCallback(async () => {
    if (!currentUser) return;
    setIsRefreshingProfile(true);
    try {
      const profile = await getUser(currentUser.uid);
      setUserProfile(profile);
    } catch (err) {
      console.warn('[WelcomeTour] Failed to refresh user profile:', err);
    } finally {
      setIsRefreshingProfile(false);
    }
  }, [currentUser]);

  // Persist the "Don't show again" preference immediately when changed
  const handleDontShowAgainChange = useCallback(async (checked: boolean) => {
    setDontShowAgain(checked);
    if (checked && currentUser && !preferencePersisted) {
      setPreferenceSaving(true);
      try {
        await markWelcomeTourCompleted(currentUser.uid);
        setPreferencePersisted(true);
      } catch (err) {
        console.error('[WelcomeTour] Failed to persist preference:', err);
      } finally {
        setPreferenceSaving(false);
      }
    }
  }, [currentUser, preferencePersisted]);

  const handleNext = useCallback(async () => {
    if (activeStep < tourSteps.length - 1) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      
      // Refresh profile when reaching profile-dependent panels (merged/not-merged)
      // This ensures automatch has completed and we show the correct panel
      const nextStepData = tourSteps[nextStep];
      if (nextStepData && (nextStepData.condition === 'merged' || nextStepData.condition === 'not-merged')) {
        refreshUserProfile();
      }
      
      // Auto-check "don't show again" on the final panel and persist it
      if (nextStep === tourSteps.length - 1) {
        setDontShowAgain(true);
        // Auto-persist when reaching final step
        if (currentUser && !preferencePersisted) {
          try {
            await markWelcomeTourCompleted(currentUser.uid);
            setPreferencePersisted(true);
          } catch (err) {
            console.error('[WelcomeTour] Failed to persist on final step:', err);
          }
        }
      }
    } else {
      setDismissed(true);
      setOpen(false);
    }
  }, [activeStep, tourSteps, currentUser, preferencePersisted, refreshUserProfile]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleSkip = useCallback(async () => {
    // If "don't show again" is checked, ensure it's persisted before closing
    if (dontShowAgain && currentUser && !preferencePersisted) {
      try {
        await markWelcomeTourCompleted(currentUser.uid);
        setPreferencePersisted(true);
      } catch (err) {
        console.error('[WelcomeTour] Failed to persist on skip:', err);
      }
    }
    setDismissed(true);
    setOpen(false);
  }, [dontShowAgain, currentUser, preferencePersisted]);

  // Handle "This is NOT me" unclaim submission
  const handleUnclaimSubmit = useCallback(async () => {
    if (!currentUser || !userProfile) return;
    
    setUnclaimSubmitting(true);
    setUnclaimError(null);
    
    try {
      const profileName = userProfile.displayName || 
        `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim() || 
        'Unknown';
      
      const result = await submitUnclaimProfileRequest({
        profileDocId: currentUser.uid,
        personId: userProfile.personId || null,
        name: profileName,
        reason: 'User indicated "This is not me" during welcome tour',
        keepPersonalDetails: true  // Keep user's account details, just disconnect from wrong profile
      });
      
      if (result.success) {
        setUnclaimSuccess(true);
      } else {
        setUnclaimError(result.error || 'Failed to submit request');
      }
    } catch (err) {
      console.error('[WelcomeTour] Failed to submit unclaim:', err);
      setUnclaimError('An error occurred. Please try again.');
    } finally {
      setUnclaimSubmitting(false);
    }
  }, [currentUser, userProfile]);

  // Handle UI language change - also sets email languagePreference implicitly
  const handleSaveUiLanguage = useCallback(async (lang: 'en' | 'no' | 'fr') => {
    if (!currentUser) return;
    setUiLanguage(lang);
    try {
      // Change UI language
      i18n.changeLanguage(lang);
      // Also save as email language preference (use 'en' as fallback for fr since we don't have fr emails)
      const emailLang = (lang === 'en' || lang === 'no') ? lang : 'en';
      await updateUserLanguagePreference(currentUser.uid, emailLang);
      setSettingsSaved(true);
    } catch (err) {
      console.error('[WelcomeTour] Failed to save language preference:', err);
    }
  }, [currentUser, i18n]);

  // Handle units change
  const handleUnitsChange = useCallback((newUnits: 'km' | 'mi') => {
    setUnits(newUnits);
    localStorage.setItem('pref_units', newUnits);
    notifyUnitsChange();
    setSettingsSaved(true);
  }, []);

  // Handle theme change
  const handleThemeChange = useCallback((newMode: 'light' | 'dark' | 'system') => {
    setThemeMode(newMode);
    setSettingsSaved(true);
  }, [setThemeMode]);

  const handleClose = useCallback(async () => {
    setOpen(false);
    // Only persist if not already persisted
    if (currentUser && !preferencePersisted && (dontShowAgain || activeStep === tourSteps.length - 1)) {
      try {
        await markWelcomeTourCompleted(currentUser.uid);
        setPreferencePersisted(true);
      } catch (err) {
        console.error('[WelcomeTour] Failed to mark tour as completed:', err);
      }
    }
  }, [currentUser, dontShowAgain, activeStep, tourSteps.length, preferencePersisted]);

  const currentStep = tourSteps[activeStep];
  const isLastStep = activeStep === tourSteps.length - 1;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          py: 2,
          px: 3,
        }}
      >
        <DialogTitle sx={{ color: 'inherit', p: 0 }}>
          <Typography variant="h5" component="div" fontWeight="bold">
            {t('welcomeTour.dialogTitle')}
          </Typography>
        </DialogTitle>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 2, minHeight: 280 }}>
        <Box sx={{ mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {tourSteps.map((step, index) => (
              <Step key={index}>
                <StepLabel />
              </Step>
            ))}
          </Stepper>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            textAlign: 'center',
            bgcolor: 'action.hover',
            borderRadius: 2,
            minHeight: 180,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Special content for profile confirmation step (merged users) */}
          {currentStep.condition === 'merged' && wasAutoMerged ? (
            <>
              <Box sx={{ mb: 2 }}>
                {isRefreshingProfile ? (
                  <CircularProgress size={48} />
                ) : profileConfirmation === 'yes' ? (
                  <CheckCircle style={{ ...iconStyle, color: theme.palette.success.main }} />
                ) : (
                  <XCircle style={{ ...iconStyle, color: theme.palette.error.main }} />
                )}
              </Box>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                {t('welcomeTour.profileLinked.title')}
              </Typography>
              
              {/* Profile preview */}
              <Box sx={{ 
                bgcolor: 'background.paper', 
                p: 2, 
                borderRadius: 1, 
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                width: '100%',
                maxWidth: 300
              }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {userProfile?.displayName || 
                    `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim() || 
                    t('common.unknown')}
                </Typography>
                {(userProfile?.dateOfBirth || userProfile?.yearOfBirth) && (
                  <Typography variant="body2" color="text.secondary">
                    {userProfile?.dateOfBirth 
                      ? new Date(userProfile.dateOfBirth).toLocaleDateString()
                      : userProfile?.yearOfBirth ? `${t('common.born')} ${userProfile.yearOfBirth}` : ''}
                  </Typography>
                )}
              </Box>

              {/* Confirmation radio buttons */}
              {!unclaimSuccess ? (
                <RadioGroup
                  value={profileConfirmation}
                  onChange={(e) => setProfileConfirmation(e.target.value as 'yes' | 'no')}
                  sx={{ alignItems: 'flex-start' }}
                >
                  <FormControlLabel
                    value="yes"
                    control={<Radio color="success" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircle size={16} color={theme.palette.success.main} />
                        <Typography>{t('welcomeTour.profileConfirm.yes')}</Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="no"
                    control={<Radio color="error" />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <XCircle size={16} color={theme.palette.error.main} />
                        <Typography>{t('welcomeTour.profileConfirm.no')}</Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              ) : (
                <Alert severity="success" sx={{ mt: 1 }}>
                  {t('welcomeTour.profileConfirm.unclaimSubmitted')}
                </Alert>
              )}

              {/* Submit unclaim button when "no" is selected */}
              {profileConfirmation === 'no' && !unclaimSuccess && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleUnclaimSubmit}
                    disabled={unclaimSubmitting}
                    startIcon={unclaimSubmitting ? <CircularProgress size={16} /> : null}
                  >
                    {unclaimSubmitting 
                      ? t('common.submitting') 
                      : t('welcomeTour.profileConfirm.submitUnclaim')}
                  </Button>
                  {unclaimError && (
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                      {unclaimError}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          ) : currentStep.titleKey === 'welcomeTour.settings.title' ? (
            /* Settings step content */
            <>
              <Box sx={{ mb: 2 }}>{currentStep.icon}</Box>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                {t(currentStep.titleKey)}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t(currentStep.descriptionKey)}
              </Typography>
              
              {/* Settings grid */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2,
                width: '100%',
                maxWidth: 400
              }}>
                {/* Language */}
                <Box sx={{ 
                  bgcolor: 'background.paper', 
                  p: 2, 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'left'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    {t('welcomeTour.settings.language')}
                  </Typography>
                  <RadioGroup
                    row
                    value={uiLanguage}
                    onChange={(e) => handleSaveUiLanguage(e.target.value as 'en' | 'no' | 'fr')}
                  >
                    <FormControlLabel
                      value="no"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">🇳🇴 Norsk</Typography>}
                    />
                    <FormControlLabel
                      value="en"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">🇬🇧 English</Typography>}
                    />
                    <FormControlLabel
                      value="fr"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">🇫🇷 Français</Typography>}
                    />
                  </RadioGroup>
                </Box>

                {/* Units */}
                <Box sx={{ 
                  bgcolor: 'background.paper', 
                  p: 2, 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'left'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    {t('welcomeTour.settings.units')}
                  </Typography>
                  <RadioGroup
                    row
                    value={units}
                    onChange={(e) => handleUnitsChange(e.target.value as 'km' | 'mi')}
                  >
                    <FormControlLabel
                      value="km"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{t('welcomeTour.settings.metric')}</Typography>}
                    />
                    <FormControlLabel
                      value="mi"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{t('welcomeTour.settings.imperial')}</Typography>}
                    />
                  </RadioGroup>
                </Box>

                {/* Theme */}
                <Box sx={{ 
                  bgcolor: 'background.paper', 
                  p: 2, 
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  textAlign: 'left'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                    {t('welcomeTour.settings.theme')}
                  </Typography>
                  <RadioGroup
                    row
                    value={themeMode}
                    onChange={(e) => handleThemeChange(e.target.value as 'light' | 'dark' | 'system')}
                  >
                    <FormControlLabel
                      value="light"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{t('welcomeTour.settings.light')}</Typography>}
                    />
                    <FormControlLabel
                      value="dark"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{t('welcomeTour.settings.dark')}</Typography>}
                    />
                    <FormControlLabel
                      value="system"
                      control={<Radio size="small" />}
                      label={<Typography variant="body2">{t('welcomeTour.settings.system')}</Typography>}
                    />
                  </RadioGroup>
                </Box>

                {settingsSaved && (
                  <Typography variant="caption" color="success.main" textAlign="center">
                    ✓ {t('welcomeTour.settings.saved')}
                  </Typography>
                )}
                
                {/* Hint about cogwheel settings menu */}
                <Typography variant="caption" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                  {t('welcomeTour.settings.changeHint')}
                </Typography>
              </Box>
            </>
          ) : (
            /* Standard step content */
            <>
              <Box sx={{ mb: 2 }}>{currentStep.icon}</Box>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                {t(currentStep.titleKey)}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                {t(currentStep.descriptionKey)}
              </Typography>
            </>
          )}
        </Paper>

        {/* Skip and Don't show again - available on all panels */}
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => handleDontShowAgainChange(e.target.checked)}
                disabled={preferenceSaving || preferencePersisted}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {t('welcomeTour.dontShowAgain')}
              </Typography>
            }
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleSkip} color="inherit" size="small">
            {t('welcomeTour.skip')}
          </Button>
          {activeStep > 0 && (
            <Button onClick={handleBack} color="inherit">
              {t('common.back')}
            </Button>
          )}
        </Box>
        <Button
          onClick={handleNext}
          variant="contained"
          color="primary"
          size="large"
          sx={{ minWidth: 120 }}
        >
          {isLastStep ? t('welcomeTour.getStarted') : t('common.next')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WelcomeTour;
